import { Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { NavbarService } from '../library/navbar.service';
import { map } from 'rxjs/operators';
import { NavbarUser } from '../library/model/navbar-user';

@Injectable({
  providedIn: 'root',
})
export class RouteCanActivateGuard implements CanActivate {
  constructor(
    @Inject('EnvironmentProvider') private environmentProvider,
    private router: Router,
    private navbarService: NavbarService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const firstSegmentUrl = this.getFirstSegmentFromUrl(state);

    return this.navbarService.getUser().pipe(
      map((customer) => {
        // customer is not cnx
        if (this.isNotConnected(customer)) {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          this.navbarService.setDynamicServiceUtagDatas({ statut_loggue: 'Non logué' });
          return this.guardUserNotConnected(firstSegmentUrl);
        }
        // eslint-disable-next-line @typescript-eslint/naming-convention
        this.navbarService.setDynamicServiceUtagDatas({ statut_loggue: 'Logué' });

        // check is a specific User

        if (
          customer.contactInfo &&
          customer.contactInfo.email &&
          customer.contactInfo.lastName &&
          customer.contactInfo.firstName
        ) {
          const authorizedUser = this.environmentProvider.authorizedUsers.find(
            (it) =>
              customer.contactInfo.email === it.email &&
              customer.contactInfo.lastName === it.lastName &&
              customer.contactInfo.firstName === it.firstName
          );

          if (authorizedUser !== undefined) {
            customer.enterprise.commercialSegment = authorizedUser.segCommercial;
          }
        }

        // check if a specific siren

        if (customer.enterprise?.siren) {
          const authorizedSiren = this.environmentProvider.authorizedSirens.find(
            (it) => customer.enterprise.siren === it.siren
          );

          if (authorizedSiren !== undefined) {
            customer.enterprise.commercialSegment = authorizedSiren.segCommercial;
          }
        }

        // customer is cnx
        return this.guardUserConnected(firstSegmentUrl, this.getBusinessSegment(customer));
      })
    );
  }

  private getBusinessSegment(customer: NavbarUser): string {
    if (customer && customer.enterprise && customer.enterprise.commercialSegment) {
      return customer.enterprise.commercialSegment;
    }
    return '';
  }

  private isNotConnected(customer: NavbarUser): boolean {
    if (customer && customer.enterprise) return false;
    return true;
  }

  private guardUserNotConnected(firstSegment: string): boolean {
    if (this.isInvalid(firstSegment)) {
      this.routerLinkRedirect(this.environmentProvider.notFoundErrorUrl);
      return false;
    }

    if (this.isRoot(firstSegment)) {
      this.routerLinkRedirect(this.environmentProvider.publicUrl);
      return false;
    }

    if (this.isUnauthorizedWhenUnconnected(firstSegment)) {
      this.routerLinkRedirect(this.environmentProvider.unauthorizedErrorUrl);
      return false;
    }

    return true;
  }

  private guardUserConnected(firstSegment: string, businessSegment: string): boolean {
    let urlRedirect;

    if (this.isInvalid(firstSegment)) {
      this.routerLinkRedirect(this.environmentProvider.notFoundErrorUrl);
      return false;
    }

    if (this.isRoot(firstSegment)) {
      const environnementProviderUrl = this.environmentProvider.authorizedFirstSegmentUrl.find(
        (value) => value.authorizedBusinessSegment === businessSegment
      );

      if (environnementProviderUrl === undefined || businessSegment === '') {
        urlRedirect = this.environmentProvider.publicUrl;
      } else {
        urlRedirect = '/' + environnementProviderUrl.firstSegmentUrl;
      }

      this.routerLinkRedirect(urlRedirect);
      return false;
    }

    if (this.isAuthorizedForAll(firstSegment)) {
      return true;
    }

    if (this.isAuthorizedForThisBusinessSegment(firstSegment, businessSegment)) {
      return true;
    }

    this.routerLinkRedirect(this.environmentProvider.unauthorizedErrorUrl);
    return false;
  }

  private isRoot(firstSegment: string): boolean {
    if (firstSegment === '') return true;
    return false;
  }

  private isUnauthorizedWhenUnconnected(firstSegment: string) {
    return !this.environmentProvider.authorizedFirstSegmentUrl.some(
      (value) => value.firstSegmentUrl === firstSegment && value.authorizedBusinessSegment === ''
    );
  }

  private isAuthorizedForAll(firstSegment: string): boolean {
    return this.environmentProvider.authorizedFirstSegmentUrl.some(
      (value) => value.authorizedBusinessSegment === '' && value.firstSegmentUrl === firstSegment
    );
  }

  private isAuthorizedForThisBusinessSegment(firstSegment: string, businessSegment: string) {
    return this.environmentProvider.authorizedFirstSegmentUrl.some(
      (value) => value.authorizedBusinessSegment === businessSegment && value.firstSegmentUrl === firstSegment
    );
  }

  private isInvalid(firstSegmentUrl: string): boolean {
    return !this.environmentProvider.authorizedFirstSegmentUrl.some(
      (value) => value.firstSegmentUrl === firstSegmentUrl
    );
  }

  private getFirstSegmentFromUrl(state): string {
    return this.extractSegmentsFromUrl(state)[0] ? this.extractSegmentsFromUrl(state)[0] : '';
  }

  private extractSegmentsFromUrl(state): string[] {
    return state.url
      .split('/')
      .map((segment) => (segment.indexOf('?') > -1 ? segment.substring(0, segment.indexOf('?')) : segment))
      .filter((value) => value !== '');
  }

  private routerLinkRedirect(url: string) {
    if (url && url.length > 1) this.router.navigate([url]);
  }
}
